// ════════════════════════════════════════════════════════════
// EstudiosOperacion — hoja de costes y cálculo de precio/margen
// Fase 1. Guardar en: src/pages/EstudiosOperacion.tsx
// ════════════════════════════════════════════════════════════
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Card, CardContent, TextField, IconButton,
  Select, MenuItem, FormControl, InputLabel, Chip, Alert, Grid,
  Table, TableBody, TableCell, TableHead, TableRow, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  getEstudios, getEstudio, crearEstudio, actualizarEstudio, borrarEstudio,
  getOportunidadesComerciales
} from '../api';
const SUGERENCIAS = [
  'Precio de compra', 'Transporte', 'Reacondicionamiento (mano de obra)',
  'Reacondicionamiento (repuestos)', 'Pintura', 'Inspección / certificación',
  'Provisión de garantía', 'Comisiones', 'Coste financiero', 'Otros'
];

const eur = (n: number) =>
  (n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

export default function EstudiosOperacion() {
  const [estudios, setEstudios] = useState<any[]>([]);
  const [plantillas, setPlantillas] = useState<any[]>([]);
  const [abierto, setAbierto] = useState<any>(null); // estudio en edición
  const [oportunidades, setOportunidades] = useState<any[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [dialogPlantillas, setDialogPlantillas] = useState(false);

  const cargar = () => {
    getEstudios(false).then(setEstudios).catch(() => setEstudios([]));
    getEstudios(true).then(setPlantillas).catch(() => setPlantillas([]));
    getOportunidadesComerciales().then(setOportunidades).catch(() => setOportunidades([]));
  };
  useEffect(() => { cargar(); }, []);

  // ── Cálculos en vivo ──
  const totalCostes = (abierto?.lineas || [])
    .reduce((s: number, l: any) => s + (parseFloat(l.importe) || 0), 0);
  const precioVenta = abierto?.modo_calculo === 'margen'
    ? (abierto?.margen_objetivo < 100 && totalCostes > 0
        ? totalCostes / (1 - (abierto?.margen_objetivo || 0) / 100) : 0)
    : (parseFloat(abierto?.precio_venta) || 0);
  const margenEuros = precioVenta - totalCostes;
  const margenPct = precioVenta > 0 ? (margenEuros / precioVenta) * 100 : 0;
  const bajoMinimo = precioVenta > 0 && margenPct < (abierto?.margen_minimo ?? 10);

  // ── Acciones ──
  const nuevoEstudio = (desde: any = null) => {
    setAbierto({
      id: null,
      titulo: desde ? `${desde.titulo} (copia)` : '',
      oportunidad_id: '',
      lineas: desde ? desde.lineas.map((l: any) => ({ ...l })) : [],
      cobros: desde ? desde.cobros : [],
      modo_calculo: desde ? desde.modo_calculo : 'margen',
      margen_objetivo: desde ? desde.margen_objetivo : 15,
      precio_venta: desde ? desde.precio_venta : 0,
      margen_minimo: desde ? desde.margen_minimo : 10,
      notas: '',
      es_plantilla: false,
    });
    setDialogPlantillas(false);
  };

  const abrirEstudio = async (id: number) => {
    const est = await getEstudio(id);
    setAbierto({ ...est, oportunidad_id: est.oportunidad_id || '' });
  };

  const guardar = async (comoPlantilla = false) => {
    if (!abierto.titulo?.trim()) { alert('Ponle un título al estudio'); return; }
    setGuardando(true);
    try {
      const datos = {
        ...abierto,
        oportunidad_id: abierto.oportunidad_id || null,
        es_plantilla: comoPlantilla ? true : abierto.es_plantilla,
        precio_venta: precioVenta,
      };
      if (abierto.id) {
        await actualizarEstudio(abierto.id, datos);
      } else {
        const res = await crearEstudio(datos);
        setAbierto({ ...abierto, id: res.id });
      }
      cargar();
      if (comoPlantilla) alert('✅ Guardado como plantilla');
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (id: number) => {
    if (!window.confirm('¿Eliminar este estudio?')) return;
    try { await borrarEstudio(id); cargar(); if (abierto?.id === id) setAbierto(null); }
    catch (e: any) { alert(e.response?.data?.detail || 'Error'); }
  };

  // ── Líneas ──
  const setLinea = (i: number, campo: string, valor: any) => {
    const lineas = [...abierto.lineas];
    lineas[i] = { ...lineas[i], [campo]: valor };
    setAbierto({ ...abierto, lineas });
  };
  const addLinea = (concepto = '') =>
    setAbierto({ ...abierto, lineas: [...abierto.lineas, { concepto, importe: '' }] });
  const delLinea = (i: number) =>
    setAbierto({ ...abierto, lineas: abierto.lineas.filter((_: any, j: number) => j !== i) });
  const moverLinea = (i: number, dir: number) => {
    const lineas = [...abierto.lineas];
    const j = i + dir;
    if (j < 0 || j >= lineas.length) return;
    [lineas[i], lineas[j]] = [lineas[j], lineas[i]];
    setAbierto({ ...abierto, lineas });
  };

  // ════════ VISTA LISTADO ════════
  if (!abierto) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>🧮 Estudios de operación</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {plantillas.length > 0 && (
              <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => setDialogPlantillas(true)}>
                Desde plantilla
              </Button>
            )}
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => nuevoEstudio()}
              sx={{ bgcolor: '#4f46e5' }}>
              Nuevo estudio
            </Button>
          </Box>
        </Box>

        {estudios.length === 0 && (
          <Alert severity="info">Aún no hay estudios. Crea el primero para estimar los costes y el precio de una operación.</Alert>
        )}

        <Grid container spacing={2}>
          {estudios.map((e: any) => (
            <Grid item xs={12} sm={6} md={4} key={e.id}>
              <Card sx={{ borderRadius: 3, cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
                onClick={() => abrirEstudio(e.id)}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography sx={{ fontWeight: 700 }}>{e.titulo}</Typography>
                    <IconButton size="small" color="error"
                      onClick={(ev) => { ev.stopPropagation(); eliminar(e.id); }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {e.oportunidad_titulo && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      🔗 {e.oportunidad_titulo}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Costes</Typography>
                      <Typography sx={{ fontWeight: 600 }}>{eur(e.total_costes)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Venta</Typography>
                      <Typography sx={{ fontWeight: 600 }}>{eur(e.precio_venta)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Margen</Typography>
                      <Typography sx={{ fontWeight: 700,
                        color: e.margen_resultante < e.margen_minimo ? '#ef4444' : '#16a34a' }}>
                        {e.margen_resultante?.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    {e.creador_nombre} · {e.actualizado_en?.slice(0, 16)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Elegir plantilla */}
        <Dialog open={dialogPlantillas} onClose={(e, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') { setDialogPlantillas(false); } }} maxWidth="xs" fullWidth>
          <DialogTitle>Nueva desde plantilla</DialogTitle>
          <DialogContent>
            {plantillas.map((p: any) => (
              <Button key={p.id} fullWidth sx={{ justifyContent: 'flex-start', mb: 1 }}
                variant="outlined"
                onClick={async () => { const full = await getEstudio(p.id); nuevoEstudio(full); }}>
                {p.titulo}
              </Button>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogPlantillas(false)}>Cancelar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // ════════ VISTA EDITOR ════════
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => setAbierto(null)}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
          {abierto.id ? 'Estudio de operación' : 'Nuevo estudio'}
        </Typography>
        <Button onClick={() => guardar(true)} disabled={guardando}>Guardar como plantilla</Button>
        <Button variant="contained" onClick={() => guardar()} disabled={guardando}
          sx={{ bgcolor: '#4f46e5' }}>
          {guardando ? 'Guardando...' : 'Guardar'}
        </Button>
      </Box>

      <Grid container spacing={2}>
        {/* Columna izquierda: datos + líneas */}
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 3, mb: 2 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={7}>
                  <TextField fullWidth label="Título del estudio *" size="small"
                    placeholder="Ej: Compra SAC600 usada 2019 — reventa"
                    value={abierto.titulo}
                    onChange={(e) => setAbierto({ ...abierto, titulo: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Oportunidad vinculada</InputLabel>
                    <Select value={abierto.oportunidad_id} label="Oportunidad vinculada"
                      onChange={(e) => setAbierto({ ...abierto, oportunidad_id: e.target.value })}>
                      <MenuItem value="">— Ninguna —</MenuItem>
                      {oportunidades.map((o: any) => (
                        <MenuItem key={o.id} value={o.id}>{o.titulo}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Líneas de coste</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                {SUGERENCIAS.map(s => (
                  <Chip key={s} label={s} size="small" onClick={() => addLinea(s)}
                    sx={{ cursor: 'pointer' }} />
                ))}
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Concepto</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 160 }}>Importe</TableCell>
                    <TableCell sx={{ width: 130 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {abierto.lineas.map((l: any, i: number) => (
                    <TableRow key={i}>
                      <TableCell>
                        <TextField fullWidth variant="standard" placeholder="Concepto libre..."
                          value={l.concepto}
                          onChange={(e) => setLinea(i, 'concepto', e.target.value)} />
                      </TableCell>
                      <TableCell>
                        <TextField variant="standard" type="number" value={l.importe}
                          onChange={(e) => setLinea(i, 'importe', e.target.value)}
                          InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => moverLinea(i, -1)}><ArrowUpwardIcon fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => moverLinea(i, 1)}><ArrowDownwardIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => delLinea(i)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button startIcon={<AddIcon />} onClick={() => addLinea()} sx={{ mt: 1 }}>
                Añadir línea
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Columna derecha: cálculo */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, position: { md: 'sticky' }, top: { md: 16 } }}>
            <CardContent>
              <Typography sx={{ fontWeight: 700, mb: 2 }}>Cálculo de precio</Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Modo</InputLabel>
                <Select value={abierto.modo_calculo} label="Modo"
                  onChange={(e) => setAbierto({ ...abierto, modo_calculo: e.target.value })}>
                  <MenuItem value="margen">Fijo el margen → calcula el precio</MenuItem>
                  <MenuItem value="precio">Fijo el precio → calcula el margen</MenuItem>
                </Select>
              </FormControl>

              {abierto.modo_calculo === 'margen' ? (
                <TextField fullWidth size="small" type="number" label="Margen objetivo"
                  value={abierto.margen_objetivo}
                  onChange={(e) => setAbierto({ ...abierto, margen_objetivo: e.target.value })}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  sx={{ mb: 2 }} />
              ) : (
                <TextField fullWidth size="small" type="number" label="Precio de venta"
                  value={abierto.precio_venta}
                  onChange={(e) => setAbierto({ ...abierto, precio_venta: e.target.value })}
                  InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                  sx={{ mb: 2 }} />
              )}

              <TextField fullWidth size="small" type="number" label="Margen mínimo aceptable"
                value={abierto.margen_minimo}
                onChange={(e) => setAbierto({ ...abierto, margen_minimo: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={{ mb: 2 }} />

              <Box sx={{ bgcolor: '#f8f8f8', borderRadius: 2, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">Total costes</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{eur(totalCostes)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">Precio de venta</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{eur(precioVenta)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">Margen</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{eur(margenEuros)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Margen %</Typography>
                  <Chip label={`${margenPct.toFixed(1)}%`}
                    sx={{ fontWeight: 800, fontSize: 16,
                      bgcolor: bajoMinimo ? '#fee2e2' : '#dcfce7',
                      color: bajoMinimo ? '#dc2626' : '#16a34a' }} />
                </Box>
              </Box>

              {bajoMinimo && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Por debajo del margen mínimo ({abierto.margen_minimo}%). Revisa costes o precio.
                </Alert>
              )}

              <TextField fullWidth multiline rows={3} size="small" label="Notas"
                value={abierto.notas || ''}
                onChange={(e) => setAbierto({ ...abierto, notas: e.target.value })}
                sx={{ mt: 2 }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}